import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { QueryClient } from '@tanstack/react-query';
import type { ComponentType } from 'react';
import { ToastProvider, ConfirmProvider } from '@/components/ui/ToastProvider';
import { AdminShell } from '@/layouts/AdminShell';
import { PageLoadingSkeleton } from '@/loading/PageLoadingSkeleton';
import { RouteTransitionLoader } from '@/loading/RouteTransitionLoader';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

async function requireAuth(): Promise<void> {
  const cached = queryClient.getQueryData(['auth', 'user']);
  if (cached) return;
  const { currentUser } = await import('@/api/auth');
  const user = await queryClient.fetchQuery({
    queryKey: ['auth', 'user'],
    queryFn: currentUser,
    staleTime: 60_000,
  });
  if (!user) {
    throw redirect({ to: '/login' as never });
  }
}

type AnySearch = Record<string, unknown>;
type ListSearch = { page: number; search: string; [key: string]: unknown };

/**
 * Plain query-string search codec (no JSON quoting).
 * Keeps URLs stable and human-readable:
 *   /orders?status=processing&page=1&search=10009&zone=1
 * instead of TanStack's default JSON form (search="10009", zone="1", empty params).
 */
function parseSearchPlain(searchStr: string): AnySearch {
  const raw = searchStr.startsWith('?') ? searchStr.slice(1) : searchStr;
  const out: Record<string, unknown> = {};
  if (!raw) return out;
  for (const [key, value] of new URLSearchParams(raw)) {
    out[key] = value;
  }
  return out;
}

function stringifySearchPlain(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    // Keep core list params even when empty so shared/bookmarked URLs stay stable:
    // /orders?status=processing&page=1&search=
    const isCore = key === 'status' || key === 'page' || key === 'search' || key === 'type';
    if (value === undefined || value === null) continue;
    if (value === '' && !isCore) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function listSearch(s: AnySearch): ListSearch {
  return {
    // Preserve extra keys (status, zone, store_id, …) as plain strings
    ...Object.fromEntries(
      Object.entries(s).map(([k, v]) => [k, v == null ? '' : String(v)]),
    ),
    page: Number(s.page || 1),
    search: String(s.search ?? ''),
  } as ListSearch;
}

const rootRoute = createRootRoute({
  component: () => (
    <>
      {/* Must live inside RouterProvider — useRouterState requires the router context. */}
      <RouteTransitionLoader />
      <ToastProvider>
        <ConfirmProvider>
          <Outlet />
        </ConfirmProvider>
      </ToastProvider>
    </>
  ),
});

function ShellPending() {
  return <PageLoadingSkeleton title="Loading page…" />;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

function ShellLayout() {
  return (
    <AdminShell>
      <Outlet />
    </AdminShell>
  );
}

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  beforeLoad: async () => {
    await requireAuth();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ShellLayout as any,
  pendingComponent: ShellPending,
  pendingMs: 150,
  pendingMinMs: 250,
});

function page(path: string, component: ComponentType | unknown) {
  return createRoute({
    getParentRoute: () => shellRoute,
    path,
    validateSearch: listSearch,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: component as any,
  });
}

function pageParam(path: string, component: ComponentType | unknown) {
  return createRoute({
    getParentRoute: () => shellRoute,
    path,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: component as any,
  });
}

/** Route-level code split: only the shell/login/dashboard ship in the entry chunk. */
function lazy(importer: () => Promise<Record<string, unknown>>, exportName: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return lazyRouteComponent(importer as any, exportName as any);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const childRoutes: any[] = [
  page('/', DashboardPage),
  page('/dashboard', DashboardPage),
  // Dispatch Management operational control center
  page('/dispatch-management/live-tracking', lazy(() => import('@/pages/dispatch/LiveTrackingPage'), 'LiveTrackingPage')),
  page('/dispatch-management/orders', lazy(() => import('@/pages/dispatch/DispatchOrdersPage'), 'DispatchOrdersPage')),
  pageParam('/dispatch-management/orders/$orderId', lazy(() => import('@/pages/orders/OrderDetailPage'), 'OrderDetailPage')),
  pageParam('/dispatch-management/live-tracking/drivers/$driverId', lazy(() => import('@/pages/drivers/DriverProfilePage'), 'DriverProfilePage')),
  // Legacy dispatch paths → new destinations
  createRoute({
    getParentRoute: () => shellRoute,
    path: '/dispatch/orders',
    beforeLoad: () => {
      throw redirect({ to: '/dispatch-management/orders' as never });
    },
  }),
  createRoute({
    getParentRoute: () => shellRoute,
    path: '/dispatch/live-tracking',
    beforeLoad: () => {
      throw redirect({ to: '/dispatch-management/live-tracking' as never });
    },
  }),
  page('/orders', lazy(() => import('@/pages/orders/OrdersPage'), 'OrdersPage')),
  pageParam('/orders/$orderId', lazy(() => import('@/pages/orders/OrderDetailPage'), 'OrderDetailPage')),
  page('/offline-payments', lazy(() => import('@/pages/orders/OfflinePaymentsPage'), 'OfflinePaymentsPage')),
  page('/refunds', lazy(() => import('@/pages/orders/RefundsPage'), 'RefundsPage')),
  page('/items', lazy(() => import('@/pages/items/ItemsPage'), 'ItemsPage')),
  pageParam('/items/$itemId', lazy(() => import('@/pages/items/ItemFormPage'), 'ItemFormPage')),
  page('/items/new', lazy(() => import('@/pages/items/ItemFormPage'), 'ItemFormPage')),
  page('/items/gallery', lazy(() => import('@/pages/items/ItemsGalleryPage'), 'ItemsGalleryPage')),
  page('/items/approvals', lazy(() => import('@/pages/items/ItemApprovalsPage'), 'ItemApprovalsPage')),
  page('/items/bulk-import', lazy(() => import('@/pages/items/ItemBulkPage'), 'ItemBulkPage')),
  page('/items/bulk-export', lazy(() => import('@/pages/items/ItemBulkPage'), 'ItemBulkPage')),
  page('/item-reviews', lazy(() => import('@/pages/items/ItemReviewsPage'), 'ItemReviewsPage')),
  page('/categories', lazy(() => import('@/pages/catalog/CategoriesPage'), 'CategoriesPage')),
  page('/categories/bulk-import', lazy(() => import('@/pages/items/ItemBulkPage'), 'ItemBulkPage')),
  page('/categories/bulk-export', lazy(() => import('@/pages/items/ItemBulkPage'), 'ItemBulkPage')),
  page('/sub-categories', lazy(() => import('@/pages/catalog/SubCategoriesPage'), 'SubCategoriesPage')),
  page('/attributes', lazy(() => import('@/pages/catalog/AttributesPage'), 'AttributesPage')),
  page('/units', lazy(() => import('@/pages/catalog/UnitsPage'), 'UnitsPage')),
  page('/addons', lazy(() => import('@/pages/catalog/AddonsPage'), 'AddonsPage')),
  page('/addon-categories', lazy(() => import('@/pages/catalog/AddonCategoriesPage'), 'AddonCategoriesPage')),
  page('/brands', lazy(() => import('@/pages/catalog/BrandsPage'), 'BrandsPage')),
  page('/common-conditions', lazy(() => import('@/pages/catalog/CommonConditionsPage'), 'CommonConditionsPage')),
  page('/stores', lazy(() => import('@/pages/stores/StoresPage'), 'StoresPage')),
  page('/stores/new', lazy(() => import('@/pages/stores/StoreFormPage'), 'StoreFormPage')),
  pageParam('/stores/$storeId', lazy(() => import('@/pages/stores/StoreFormPage'), 'StoreFormPage')),
  pageParam('/stores/$storeId/detail', lazy(() => import('@/pages/stores/StoreDetailPage'), 'StoreDetailPage')),
  page('/stores/pending', lazy(() => import('@/pages/stores/StoreApplicationsPage'), 'StoreApplicationsPage')),
  page('/stores/recommended', lazy(() => import('@/pages/stores/StoreApplicationsPage'), 'StoreApplicationsPage')),
  page('/stores/bulk-import', lazy(() => import('@/pages/stores/StoreBulkPage'), 'StoreBulkPage')),
  page('/stores/bulk-export', lazy(() => import('@/pages/stores/StoreBulkPage'), 'StoreBulkPage')),
  page('/stores/banners', lazy(() => import('@/pages/marketing/BannersPage'), 'BannersPage')),
  page('/drivers', lazy(() => import('@/pages/drivers/DriversPage'), 'DriversPage')),
  pageParam('/drivers/$driverId', lazy(() => import('@/pages/drivers/DriverProfilePage'), 'DriverProfilePage')),
  page('/users', lazy(() => import('@/pages/users/UserOverviewPage'), 'UserOverviewPage')),
  page('/deliverymen', lazy(() => import('@/pages/deliverymen/DeliverymenPage'), 'DeliverymenPage')),
  page('/deliverymen/new', lazy(() => import('@/pages/deliverymen/DeliverymanFormPage'), 'DeliverymanFormPage')),
  pageParam('/deliverymen/$deliverymanId', lazy(() => import('@/pages/deliverymen/DeliverymanFormPage'), 'DeliverymanFormPage')),
  pageParam('/deliverymen/$deliverymanId/detail', lazy(() => import('@/pages/deliverymen/DeliverymanDetailPage'), 'DeliverymanDetailPage')),
  page('/deliverymen/pending', lazy(() => import('@/pages/deliverymen/DeliverymenPage'), 'DeliverymenPage')),
  page('/deliverymen/reviews', lazy(() => import('@/pages/items/ItemReviewsPage'), 'ItemReviewsPage')),
  page('/vehicles', lazy(() => import('@/pages/deliverymen/VehiclesPage'), 'VehiclesPage')),
  page('/customers', lazy(() => import('@/pages/customers/CustomersPage'), 'CustomersPage')),
  pageParam('/customers/$customerId', lazy(() => import('@/pages/customers/CustomerDetailPage'), 'CustomerDetailPage')),
  page('/customers/wallet', lazy(() => import('@/pages/customers/CustomerWalletPage'), 'CustomerWalletPage')),
  page('/customers/loyalty', lazy(() => import('@/pages/customers/CustomerLoyaltyPage'), 'CustomerLoyaltyPage')),
  page('/campaigns/basic', lazy(() => import('@/pages/marketing/CampaignsPage'), 'CampaignsPage')),
  page('/campaigns/item', lazy(() => import('@/pages/marketing/CampaignsPage'), 'CampaignsPage')),
  page('/banners', lazy(() => import('@/pages/marketing/BannersPage'), 'BannersPage')),
  page('/other-banners', lazy(() => import('@/pages/marketing/OtherBannersPage'), 'OtherBannersPage')),
  page('/coupons', lazy(() => import('@/pages/marketing/CouponsPage'), 'CouponsPage')),
  page('/notifications', lazy(() => import('@/pages/marketing/NotificationsPage'), 'NotificationsPage')),
  page('/advertisements', lazy(() => import('@/pages/marketing/AdvertisementsPage'), 'AdvertisementsPage')),
  page('/advertisements/new', lazy(() => import('@/pages/marketing/AdvertisementsPage'), 'AdvertisementsPage')),
  page('/advertisements/requests', lazy(() => import('@/pages/marketing/AdvertisementsPage'), 'AdvertisementsPage')),
  page('/flash-sales', lazy(() => import('@/pages/marketing/FlashSalesPage'), 'FlashSalesPage')),
  page('/cashback', lazy(() => import('@/pages/marketing/CashbackPage'), 'CashbackPage')),
  page('/wallet-bonus', lazy(() => import('@/pages/marketing/WalletBonusPage'), 'WalletBonusPage')),
  page('/pos', lazy(() => import('@/pages/pos/PosPage'), 'PosPage')),
  page('/transactions/account', lazy(() => import('@/pages/finance/TransactionsPage'), 'TransactionsPage')),
  page('/transactions-reports', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/overview', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/customer-payments', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/connected-accounts', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/connected-accounts/$id', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/transfers', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/transfers/vendors', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/transfers/drivers', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/payouts', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/refunds', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/webhooks', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/reports', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/transactions-reports/settings', lazy(() => import('@/pages/finance/stripe/FinancePage'), 'FinancePage')),
  page('/withdrawals/stores', lazy(() => import('@/pages/finance/WithdrawalsPage'), 'WithdrawalsPage')),
  page('/withdrawals/deliverymen', lazy(() => import('@/pages/finance/WithdrawalsPage'), 'WithdrawalsPage')),
  page('/withdraw-methods', lazy(() => import('@/pages/finance/WithdrawMethodsPage'), 'WithdrawMethodsPage')),
  page('/disbursements/stores', lazy(() => import('@/pages/finance/DisbursementsPage'), 'DisbursementsPage')),
  page('/disbursements/deliverymen', lazy(() => import('@/pages/finance/DisbursementsPage'), 'DisbursementsPage')),
  page('/dm-earnings', lazy(() => import('@/pages/finance/DmEarningsPage'), 'DmEarningsPage')),
  page('/reports', lazy(() => import('@/pages/reports/ReportsPage'), 'ReportsPage')),
  pageParam('/reports/$reportType', lazy(() => import('@/pages/reports/ReportsPage'), 'ReportsPage')),
  page('/employees', lazy(() => import('@/pages/users/EmployeesPage'), 'EmployeesPage')),
  page('/roles', lazy(() => import('@/pages/users/RolesPage'), 'RolesPage')),
  page('/contacts', lazy(() => import('@/pages/users/ContactsPage'), 'ContactsPage')),
  page('/zones', lazy(() => import('@/pages/settings/ZonesPage'), 'ZonesPage')),
  page('/surge-pricing', lazy(() => import('@/pages/settings/SurgePricingPage'), 'SurgePricingPage')),
  page('/subscriptions', lazy(() => import('@/pages/settings/SubscriptionsPage'), 'SubscriptionsPage')),
  page('/settings', lazy(() => import('@/pages/settings/SettingsLayout'), 'SettingsLayout')),
  page('/settings/business', lazy(() => import('@/pages/settings/BusinessSetupPage'), 'BusinessSetupPage')),
  page('/settings/orders', lazy(() => import('@/pages/settings/OrderSettingsPage'), 'OrderSettingsPage')),
  page('/settings/payments', lazy(() => import('@/pages/settings/PaymentSettingsPage'), 'PaymentSettingsPage')),
  page('/settings/offline-payments', lazy(() => import('@/pages/settings/OfflinePaymentMethodsPage'), 'OfflinePaymentMethodsPage')),
  page('/settings/integrations', lazy(() => import('@/pages/settings/IntegrationSettingsPage'), 'IntegrationSettingsPage')),
  page('/settings/analytics', lazy(() => import('@/pages/settings/AnalyticsSettingsPage'), 'AnalyticsSettingsPage')),
  page('/settings/email', lazy(() => import('@/pages/settings/EmailSettingsPage'), 'EmailSettingsPage')),
  page('/settings/notifications', lazy(() => import('@/pages/settings/NotificationSettingsPage'), 'NotificationSettingsPage')),
  page('/settings/fcm', lazy(() => import('@/pages/settings/FcmSettingsPage'), 'FcmSettingsPage')),
  page('/settings/social', lazy(() => import('@/pages/settings/SocialSettingsPage'), 'SocialSettingsPage')),
  page('/settings/landing', lazy(() => import('@/pages/settings/LandingSettingsPage'), 'LandingSettingsPage')),
  page('/settings/languages', lazy(() => import('@/pages/settings/LanguageSettingsPage'), 'LanguageSettingsPage')),
  page('/settings/modules', lazy(() => import('@/pages/settings/ModulesPage'), 'ModulesPage')),
  page('/settings/addons', lazy(() => import('@/pages/settings/AddonsSettingsPage'), 'AddonsSettingsPage')),
  page('/settings/files', lazy(() => import('@/pages/settings/FileManagerPage'), 'FileManagerPage')),
  pageParam('/settings/policies/$policyKey', lazy(() => import('@/pages/settings/PolicyEditorPage'), 'PolicyEditorPage')),
  page('/messages', lazy(() => import('@/pages/messages/MessagesPage'), 'MessagesPage')),
  page('/parcels', lazy(() => import('@/pages/parcel/ParcelOrdersPage'), 'ParcelOrdersPage')),
  pageParam('/parcels/$orderId', lazy(() => import('@/pages/parcel/ParcelDetailPage'), 'ParcelDetailPage')),
  page('/parcel-categories', lazy(() => import('@/pages/parcel/ParcelCategoriesPage'), 'ParcelCategoriesPage')),
  page('/parcel-settings', lazy(() => import('@/pages/parcel/ParcelSettingsPage'), 'ParcelSettingsPage')),
];

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  component: NotFoundPage,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const routeTree: any = rootRoute.addChildren([
  loginRoute,
  shellRoute.addChildren(childRoutes),
  notFoundRoute,
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const router: any = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingMs: 150,
  defaultPendingMinMs: 250,
  defaultPendingComponent: ShellPending,
  parseSearch: parseSearchPlain,
  stringifySearch: stringifySearchPlain,
});

declare module '@tanstack/react-router' {
  interface Register {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    router: any;
  }
}

export type { ListSearch };
